import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { MessageSquare, Paperclip, Send, StickyNote } from 'lucide-react';

import { Macro } from '../types';

import QuickReplyDropdown from './QuickReplyDropdown';

// Same trigger rules as telescope's QuickResponsePopover: "/" as the FIRST
// character opens the picker, the rest of the text filters by substring against
// title or body, and "/" alone shows everything.
export const matchingMacros = (macros: Macro[], text: string): Macro[] => {
  if (text.length === 0 || text.charAt(0) !== '/') return [];
  const query = text.substr(1).toLowerCase();
  return macros.filter(
    (macro) => macro.title.toLowerCase().includes(query) || macro.body.toLowerCase().includes(query)
  );
};

const MAX_TEXTAREA_HEIGHT_PX = 200; // ~8 rows, same ceiling as telescope's autosize

// Mirrors the server's allowlist; the picker filters first so most wrong
// files never reach the 415.
const ACCEPTED_FILES =
  'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,application/pdf';

type Props = {
  conversationId: string;
  // The canned first reply, or null when this conversation doesn't get one.
  greeting: string | null;
  isSending: boolean;
  macros: Macro[];
  onSend: (body: string) => Promise<void>;
  onSendNote: (body: string) => Promise<void>;
  onSendFile: (file: File) => Promise<void>;
  onManageMacros: () => void;
};

// Mounted per conversation (Thread keys it by conversation id): draft and
// mode die with the thread they belong to, so half a note never lands in the
// next customer's reply box — and a failed send has nowhere stale to restore
// into.
const Composer: React.FC<Props> = ({
  conversationId,
  greeting,
  isSending,
  macros,
  onSend,
  onSendNote,
  onSendFile,
  onManageMacros,
}: Props) => {
  const [draft, setDraft] = useState('');
  const [isListOpen, setIsListOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // What the box currently writes: a reply the customer receives, or a note
  // only the team sees.
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // What the box holds right now and which text (if any) was seeded rather
  // than typed. Refs, not effect deps: the seeding below runs on conversation
  // changes, and neither typing nor an untouched seed should re-trigger it.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const seeded = useRef<string | null>(null);

  // Seed the greeting when the agent lands on the conversation, and only into
  // an empty box (or over a previous seed left untouched): a draft the agent
  // wrote is theirs, wherever it came from. The seed is also taken back the
  // moment it stops applying — the agent switches to an answered or closed
  // thread, or a colleague answers this one over the stream — because the
  // feature trains people to hit Enter without reading, and a greeting after
  // the customer was already helped is worse than none. A seed the agent
  // edited is a draft like any other and stays.
  useEffect(() => {
    if (greeting !== null) {
      if (draftRef.current === '' || draftRef.current === seeded.current) {
        setDraft(greeting);
        seeded.current = greeting;
        // Seeding and focusing together is what makes "one Enter" true when
        // the agent arrives from a click on the conversation list.
        inputRef.current?.focus();
      }
      return;
    }
    if (seeded.current !== null && draftRef.current === seeded.current) {
      setDraft('');
    }
    seeded.current = null;
  }, [conversationId, greeting]);

  // Grow with the text like telescope's TextareaAutosize, without the package:
  // reset to auto so it can also shrink, then take the scroll height.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }, [draft]);

  const slashMatches = matchingMacros(macros, draft);

  const send = async (): Promise<void> => {
    const body = draft.trim();
    if (!body || isSending) return;

    setDraft('');
    try {
      await (mode === 'note' ? onSendNote(body) : onSend(body));
    } catch {
      // Give the text back rather than losing what they typed.
      setDraft(body);
    }
  };

  // "/" picks by replacing the whole draft (the slash was never part of the
  // message); the panel button also replaces, which keeps one rule.
  const applyMacro = (body: string): void => {
    setDraft(body);
    setIsListOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="bg-white-0.0 flex content-end flex-shrink-0 relative border-t border-white-2.0 p-3 gap-2 items-end">
      {/* Exclusive: typing "/" while the panel is open would stack two
          dropdowns on the same spot, leaving the bottom one unreachable. */}
      {slashMatches.length > 0 ? (
        <QuickReplyDropdown
          macros={slashMatches}
          onSelect={applyMacro}
          extraClassName="bottom-full mb-2 left-3"
        />
      ) : (
        isListOpen && (
          <QuickReplyDropdown
            macros={macros}
            onSelect={applyMacro}
            onManage={() => {
              setIsListOpen(false);
              onManageMacros();
            }}
            extraClassName="bottom-full mb-2 left-3"
          />
        )
      )}

      <button
        type="button"
        title="Quick replies (or type / in the message)"
        aria-label="Quick replies"
        onClick={() => setIsListOpen((open) => !open)}
        className={classNames(
          'p-2 rounded transition-colors',
          isListOpen ? 'bg-white-2.0 text-primary-2.0' : 'text-gray-2.0 hover:bg-white-1.0'
        )}
      >
        <MessageSquare size={20} />
      </button>

      <label className="sr-only" htmlFor="support-attach">
        Attach a file
      </label>
      <input
        id="support-attach"
        ref={fileInput}
        type="file"
        accept={ACCEPTED_FILES}
        className="hidden"
        onChange={(event) => {
          const picker = event.currentTarget;
          const file = picker.files?.[0];
          // Cleared right away so picking the same file twice fires again.
          picker.value = '';
          if (file) void onSendFile(file);
        }}
      />
      <button
        type="button"
        // Attachments always go out to the customer, and the note box promises
        // the opposite; while it's yellow the clip stays off rather than
        // betraying that promise.
        title={
          mode === 'note'
            ? 'Attachments go to the customer — switch back to reply'
            : 'Attach a file'
        }
        aria-label="Attach a file"
        disabled={isSending || mode === 'note'}
        onClick={() => fileInput.current?.click()}
        className="p-2 rounded transition-colors text-gray-2.0 hover:bg-white-1.0 disabled:opacity-50"
      >
        <Paperclip size={20} />
      </button>

      <button
        type="button"
        title={mode === 'note' ? 'Back to replying' : 'Write an internal note'}
        aria-label={mode === 'note' ? 'Back to replying' : 'Write an internal note'}
        onClick={() => setMode((current) => (current === 'note' ? 'reply' : 'note'))}
        className={classNames(
          'p-2 rounded transition-colors',
          mode === 'note' ? 'bg-yellow-2.0 text-black-1.0' : 'text-gray-2.0 hover:bg-white-1.0'
        )}
      >
        <StickyNote size={20} />
      </button>

      <label className="sr-only" htmlFor="support-reply">
        {/* The yellow doesn't get announced; the accessible name has to say
            which of the two boxes this is. */}
        {mode === 'note' ? 'Internal note' : 'Reply'}
      </label>
      <textarea
        id="support-reply"
        ref={inputRef}
        rows={1}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            // With the picker open the draft is a search query, not a message:
            // Enter must not send "/horario" to the customer.
            if (slashMatches.length > 0) return;
            void send();
          }
        }}
        placeholder={
          mode === 'note'
            ? 'Write an internal note… (only the team sees it)'
            : 'Write a reply… ( / for quick replies)'
        }
        className={classNames(
          'flex flex-grow rounded py-3 px-4 appearance-none leading-normal text-black-1.0 resize-none focus:outline-none',
          mode === 'note' ? 'bg-yellow-2.0' : 'bg-white-1.0'
        )}
      />

      <button
        type="button"
        aria-label="Send"
        disabled={isSending || !draft.trim()}
        onClick={() => void send()}
        className="p-2 rounded text-secondary-1.0 transition duration-150 ease-in-out hover:bg-white-1.0 disabled:opacity-50"
      >
        <Send size={20} />
      </button>
    </div>
  );
};

export default Composer;
